import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import { AppShell } from '@/shared/ui';
import { AdminHeader } from '@/widgets/admin-header';
import { AdminSidebar, MOBILE_DRAWER_ID } from '@/widgets/admin-sidebar';

export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'admin.sidebar.collapsed';

const readStoredCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    // 프라이버시 모드 등 storage 접근 불가 시 기본값(펼침)으로 동작한다.
    return false;
  }
};

/**
 * 관리자 공통 레이아웃 라우트 — 헤더 + 사이드바 GNB + 본문(Outlet).
 *
 * 레이아웃 라우트는 페이지 이동에도 유지되므로 사이드바 상태를 여기서 소유한다.
 * 접힘 여부는 localStorage 에 남겨 새로고침 후에도 이어진다.
 */
export const AdminLayout = (): ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState(readStoredCollapsed);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(isCollapsed));
    } catch {
      // 저장 실패는 무시 — 접힘 상태는 이번 세션 안에서만 유지된다.
    }
  }, [isCollapsed]);

  const handleToggleCollapse = useCallback(() => setIsCollapsed((current) => !current), []);
  const handleMobileOpen = useCallback(() => setIsMobileOpen(true), []);
  const handleMobileClose = useCallback(() => setIsMobileOpen(false), []);

  return (
    <AppShell
      // 본문이 길어도 사이드바·헤더가 화면에 고정되도록 뷰포트 잠금 레이아웃을 쓴다.
      lockViewport
      // 연회색 본문 바탕 위에 흰 카드(표·섹션)를 띄운다 — 흑연 사이드바·흰 헤더와 층이 나뉜다.
      mainClassName="bg-neutral-100 p-6 lg:p-8"
      header={
        <AdminHeader
          onMobileMenuOpen={handleMobileOpen}
          isMobileMenuOpen={isMobileOpen}
          mobileMenuId={MOBILE_DRAWER_ID}
        />
      }
      sidebar={
        <AdminSidebar
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
          isMobileOpen={isMobileOpen}
          onMobileClose={handleMobileClose}
        />
      }
    >
      <Outlet />
    </AppShell>
  );
};
